import { h } from '@omni-torn/ui';
import { DELEGATE_PERM_INFO, DELEGATE_PERMS, type DelegateChange, type DelegatePerm } from '@omni-torn/shared';
import type { FactionMember } from '../read';
import { server } from '../session';
import { ago, box, ext, loading, problem, profileUrl, withTableCsv } from '../ui';

/**
 * Faction → Delegates (owner's call 2026-11-02 15:36: "Leader/ co leader should basically get a tier panel of their own for
 * their faction for each user"): for the leader and co-leader, every member with a box per permission (packages/shared
 * faction-delegates.ts) — tick, Save — and who changed what, when. Others see what they were given.
 * Data: GET/PUT /api/faction/delegates (apps/api faction-delegates.ts; the Discord bot reads the same list).
 */

interface Answer {
  leader: boolean;
  mine?: DelegatePerm[];
  members?: Record<string, DelegatePerm[]>;
  log?: DelegateChange[];
}

let built: HTMLElement | null = null;
/** A new visit to the Faction page reads it afresh. */
export function resetDelegates(): void {
  built = null;
}

const permName = (p: DelegatePerm) => DELEGATE_PERM_INFO[p].name;

export function delegatesSection(roster: () => FactionMember[]): HTMLElement {
  if (built) return built;
  const holder = h('div', { 'data-card': 'delegates' }, loading('Reading your faction’s delegates…'));
  const draw = (a: Answer) => {
    if (!a.leader) {
      const mine = a.mine ?? [];
      holder.replaceChildren(
        h('p', { class: 'notice', 'data-delegates': 'leaders' }, 'Your faction’s leader and co-leader choose here what each member may do for the faction (Discord servers, the bot’s settings, OC rules, the bank, …).'),
        h('p', { 'data-delegates': 'mine' }, mine.length ? `They allowed you: ${mine.map(permName).join(', ')}.` : 'You haven’t been given anything extra.'),
      );
      return;
    }
    const given = a.members ?? {};
    const names = new Map(roster().map((m) => [m.id, m]));
    // Everyone on the member list (leaders always have everything), plus delegates no longer on it (they can be cleared).
    const ids = [...new Set([...roster().map((m) => m.id), ...Object.keys(given).map(Number)])];
    const boxes = new Map<number, Map<DelegatePerm, HTMLInputElement>>();
    const status = h('p', { class: 'save-status small', 'data-delegates': 'status' });
    const rows = ids
      .map((id) => ({ id, m: names.get(id), lead: /^(leader|co-leader)$/i.test(names.get(id)?.position ?? '') }))
      .sort((x, y) => Number(y.lead) - Number(x.lead) || (x.m?.name ?? '').localeCompare(y.m?.name ?? ''))
      .map(({ id, m, lead }) => {
        const mine = new Map<DelegatePerm, HTMLInputElement>();
        boxes.set(id, mine);
        return h(
          'tr',
          { 'data-member': String(id) },
          h('td', {}, ext(profileUrl(id), m?.name ?? `#${id}`), h('span', { class: 'muted small' }, ` ${m ? m.position : '(not in the faction now)'}`)),
          ...DELEGATE_PERMS.map((p) => {
            const c = h('input', { type: 'checkbox', checked: lead || (given[String(id)] ?? []).includes(p), disabled: lead, title: lead ? 'Leaders always may' : DELEGATE_PERM_INFO[p].what, 'data-perm': p }) as HTMLInputElement;
            if (!lead) mine.set(p, c);
            return h('td', { class: 'c' }, c);
          }),
        );
      });
    const save = h('button', { type: 'button', class: 'btn-primary', 'data-delegates': 'save' }, 'Save') as HTMLButtonElement;
    save.addEventListener('click', () => {
      const members: Record<string, DelegatePerm[]> = {};
      for (const [id, m] of boxes) {
        const perms = DELEGATE_PERMS.filter((p) => m.get(p)?.checked);
        if (perms.length) members[String(id)] = perms;
      }
      status.className = 'save-status small';
      status.textContent = 'Saving…';
      void server<Answer>('PUT', '/api/faction/delegates', { members })
        .then((x) => {
          draw(x);
          const st = holder.querySelector('[data-delegates=status]');
          if (st) (st.className = 'save-status small ok'), (st.textContent = 'Saved.');
        })
        .catch((err: Error) => {
          status.className = 'save-status small bad';
          status.textContent = err.message;
        });
    });
    const log = a.log ?? [];
    holder.replaceChildren(
      box('Delegates: what each member may do', [
        h('p', { class: 'muted small' }, 'The leader and co-leader may always do everything; anyone else only what you tick here. Choosing delegates stays with the leader and co-leader. The Discord bot follows the same list.'),
        withTableCsv(h('div', { class: 'table-wrap' }, h('table', {}, h('thead', {}, h('tr', {}, h('th', {}, 'Member'), ...DELEGATE_PERMS.map((p) => h('th', { class: 'c', title: DELEGATE_PERM_INFO[p].what }, permName(p))))), h('tbody', {}, ...rows)))),
        h('div', {}, save, ' ', status),
        h('details', {}, h('summary', {}, 'What each one allows'), h('ul', { class: 'plain-list small' }, ...DELEGATE_PERMS.map((p) => h('li', {}, h('strong', {}, permName(p)), ` — ${DELEGATE_PERM_INFO[p].what}`)))),
      ]),
      box('Changes', [
        log.length
          ? h(
              'ul',
              { class: 'plain-list small', 'data-delegates': 'log' },
              ...log.slice(0, 30).map((e) => h('li', {}, `${ago(e.at)} — ${e.by ? `${e.by.name} [${e.by.id}]` : 'Omni-Torn (brought in from an older list)'}: ${e.player ? `${names.get(e.player)?.name ?? `#${e.player}`} [${e.player}]` : 'a member'} → ${e.perms.length ? e.perms.map(permName).join(', ') : 'nothing'}`)),
            )
          : h('p', { class: 'muted', 'data-delegates': 'log' }, 'No changes yet.'),
      ]),
    );
  };
  void server<Answer>('GET', '/api/faction/delegates')
    .then(draw)
    .catch((err) => holder.replaceChildren(problem(err)));
  built = holder;
  return holder;
}
